import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState, StatusBar } from "react-native";
import moment from "moment";
import { cancelAllNotifications } from "@microdoing/shared/services/notifService";
import {
  getClientId,
  getUserFirstName,
  getUserId,
} from "@microdoing/shared/services/user/selectors";
import {
  getCurrentUserCampaignRequest,
  getEventsRequest,
} from "@microdoing/shared/services/userCampaign/actions";
import EndSessionModal from "../EndSessionModal";
import {
  getCampaignStartDate,
  getUserCampaignTeamRank,
  getUserCampaignRank,
  getCampaignPoints,
  getUserCampaignIsDone,
  getUserCampaignIsRemoved,
  getCampaignEndDate,
  getUserCampaignId,
} from "@microdoing/shared/services/userCampaign/selectors";
import { updateTime } from "@microdoing/shared/services/general/actions";
import { getTime } from "@microdoing/shared/services/general/selectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsDailyFocusScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { Box, MySafeAreaView } from "app/components/common";
import theme from "app/Theme/Theme";
import TimelineActivation from "./TimelineActivation/TimelineActivation";
import getDeviceId from "@microdoing/shared/services/user/getDeviceId";
import { addDeviceId } from "@microdoing/shared/services/user/actions";
import { getClientRequest } from "@microdoing/shared/services/client/actions";
import messaging from "@react-native-firebase/messaging";
import { getIsCoachingFinished } from "@microdoing/shared/services/coaching/selectors";
import NoCampaign from "./NoCampaign";
import FinishedCampaign from "./FinishedCampaign";
import NotStartedCampaign from "./NotStartedCampaign";
import { getOnboardingSurveyRequest } from "@microdoing/shared/services/survey/actions";

import { getAchievement } from "@microdoing/shared/services/notification/selectors";
import i18n from "@microdoing/shared/i18n";
import { seeAchievement } from "@microdoing/shared/services/notification/actions";
import { getIsValidateRequesting } from "@microdoing/shared/services/userCampaign/selectors";
import AchievementModal from "app/components/common/AchievementModal";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const DailyFocusScreen = () => {
  const [isEndSessionModalVisible, setIsEndSessionModalVisible] = useState<
    boolean
  >(false);
  const [hasSeenEndModal, setHasSeenEndModal] = useState<boolean>(false);

  const userId = useSelector(getUserId);
  const clientId = useSelector(getClientId);
  const teamRank = useSelector(getUserCampaignTeamRank);
  const userRank = useSelector(getUserCampaignRank);
  const points = useSelector(getCampaignPoints);
  const firstName = useSelector(getUserFirstName);
  const isDone = useSelector(getUserCampaignIsDone);
  const isRemoved = useSelector(getUserCampaignIsRemoved);
  const startDate = useSelector(getCampaignStartDate);
  const endDate = useSelector(getCampaignEndDate);
  const time = useSelector(getTime);

  const achievement = useSelector(getAchievement);
  const isValidateRequesting = useSelector(getIsValidateRequesting);

  const userCampaignId = useSelector(getUserCampaignId);
  const deviceId = useSelector(getDeviceId);

  const insets = useSafeAreaInsets();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getEventsRequest({ campaignId: userCampaignId }));

    dispatch(getClientRequest(clientId));

    const fcmToken = getToken();

    if (deviceId !== fcmToken) {
      dispatch(addDeviceId({ userId: userId }));
    }

    messaging().onTokenRefresh(token => {
      if (userId) {
        dispatch(addDeviceId({ userId: userId }));
      }
    });

    dispatch(getCurrentUserCampaignRequest({ userId }));

    if (isCampaignFinished()) {
      cancelAllNotifications();
      if (!hasSeenEndModal) {
        setHasSeenEndModal(true);
        setIsEndSessionModalVisible(true);
      }
    }

    const appState = AppState.addEventListener("change", onOpenApp);

    return () => {
      appState.remove();
    };
  }, []);

  useEffect(() => {
    dispatch(getCurrentUserCampaignRequest({ userId: userId }));
    if (isCampaignFinished()) {
      cancelAllNotifications();
      if (!hasSeenEndModal) {
        setHasSeenEndModal(true);
        setIsEndSessionModalVisible(true);
      }
    }
  }, [startDate, endDate, isDone]);

  const getToken = async () => {
    const fcmToken = await messaging().getToken();
    return fcmToken;
  };

  const onOpenApp = appstate => {
    if (!isWebDesktop && appstate !== "active" && appstate !== "background")
      return;
    if (appstate === "active") {
      dispatch(updateTime(moment().toDate()));
    }
  };

  const refresh = () => {
    dispatch(getCurrentUserCampaignRequest({ userId: userId }));
  };

  const isCampaignFinished = () => {
    // return false;
    if (isDone || isRemoved) return true;

    if (!endDate) {
      return false;
    }
    const now = moment(time);
    return now > moment(endDate).endOf("day");
  };

  const isCampaignNotStarted = () => {
    if (!startDate) {
      return false;
    }
    const now = moment(time);
    return moment(startDate).startOf("day").isAfter(now);
  };

  const displayScreen = () => {
    if (!userCampaignId) {
      return <NoCampaign onPressRefresh={refresh} />;
    } else if (isCampaignFinished()) {
      return <FinishedCampaign />;
    } else if (isCampaignNotStarted()) {
      return <NotStartedCampaign />;
    }
    return <TimelineActivation />;
  };

  const closeEndSessionModal = useCallback(() => {
    setIsEndSessionModalVisible(false);
  }, []);

  const handleSeeAchievement = useCallback(() => {
    dispatch(seeAchievement());
  }, []);

  const isAchievementModalVisible = useMemo(() => {
    return (
      achievement?.name !== "" &&
      achievement?.name !== undefined &&
      !isValidateRequesting
    );
  }, [achievement, isValidateRequesting]);

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsDailyFocusScreen}>
      <Box
        style={{ paddingTop: insets.top }}
        flex={1}
        backgroundColor={isWebDesktop ? undefined : "neutral.75"}
        testID="current-track-screen"
      >
        <StatusBar
          animated
          barStyle="dark-content"
          backgroundColor={theme.colors["neutral.75"]}
        />
        <AchievementModal
          isVisible={isAchievementModalVisible}
          isDone
          title={i18n.t("feat-unlocked")}
          triggerModal={handleSeeAchievement}
          {...achievement}
        />
        <EndSessionModal
          rank={userRank}
          teamRank={teamRank}
          experience={points}
          firstName={firstName}
          isVisible={isEndSessionModalVisible}
          triggerModal={closeEndSessionModal}
        />
        <Box flex={1}>{displayScreen()}</Box>
      </Box>
    </WebDesktopView>
  );
};

export default memo(DailyFocusScreen);
